import React, { useCallback, useEffect, useState } from 'react';
import { Button, Divider, List, Popover, Space, Spin, Tag, Typography } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import dayjs from 'dayjs';
import {
  UnverifiedAddressItem,
  getUnverifiedAddresses,
  onUnverifiedAddressesUpdated,
} from '@/services/app/tenant/dispatch/dispatch';
import { ensureNotificationSocketConnected } from '@/services/app/websocket';
import { useJitteredReload } from '@/hooks/useJitteredReload';
import PinAddressModal from '@/pages/tenant/dispatch/DispatchDashboard/components/PinAddressModal';
import styles from './index.less';

/** Safety-net re-fetch for a push that never arrived (visible tabs only). */
const SAFETY_POLL_MS = 5 * 60 * 1000;

/**
 * Nav-bar badge for active jobs whose map pin is only the geocoder's nearest
 * guess (customer_property_geolocation.accuracy = approximate): the importer
 * kept the warranty company's address text but Google could not verify the
 * location. Visible to ALL tenant users.
 *
 * State-driven, not notification-driven (mirrors AutoScheduleBadge): the list
 * is the set of unverified pins on non-terminal jobs, so it can't be
 * "marked read" away and clears itself the moment someone pins the location
 * (accuracy=manual). Live-refreshed by its own WS push
 * (UNVERIFIED_ADDRESSES_UPDATED, sent only when the list's inputs change),
 * not by every notifications-updated push.
 * Hidden entirely while there is nothing to verify.
 */
const UnverifiedAddressBadge: React.FC = () => {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems]     = useState<UnverifiedAddressItem[]>([]);
  const [count, setCount]     = useState(0);
  const [pinItem, setPinItem] = useState<UnverifiedAddressItem | null>(null);

  const fetchItems = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const data = await getUnverifiedAddresses();
      setItems(data?.items ?? []);
      setCount(data?.count ?? 0);
    } catch {
      // Silent — the badge is informational; the next refresh retries.
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  // Every open tab of the tenant gets the push at once — spread the re-fetches
  // and fold a burst into one (the reconnect resync comes through here too).
  const refetchFromPush = useJitteredReload(fetchItems);

  useEffect(() => {
    fetchItems();
    ensureNotificationSocketConnected();
    return onUnverifiedAddressesUpdated(refetchFromPush);
  }, [fetchItems, refetchFromPush]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchItems();
    }, SAFETY_POLL_MS);
    return () => clearInterval(timer);
  }, [fetchItems]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) fetchItems(true);
  }, [fetchItems]);

  const openCustomer = (item: UnverifiedAddressItem) => {
    setOpen(false);
    if (item.customer_id) {
      history.push(`/tenant/sales-crm/customer/detail/${item.customer_id}`);
    }
  };

  // Nothing pending — keep the topbar clean. The pin modal must stay mounted
  // while saving even if the last item is being resolved.
  if (count === 0 && !pinItem) return null;

  const content = (
    <div style={{ width: 420 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Text strong>Unverified Addresses — Pin the Exact Location</Typography.Text>
        <Tag color="orange">{count}</Tag>
      </div>

      <Spin spinning={loading}>
        <List
          dataSource={items}
          style={{ maxHeight: 420, overflowY: 'auto' }}
          renderItem={(item) => (
            <List.Item style={{ padding: '10px 4px', display: 'block', borderBottom: '1px solid #f0f0f0' }}>
              <Typography.Text strong style={{ fontSize: 13 }}>
                {item.property?.full_address || item.customer_name || `Job #${item.dispatch_id}`}
              </Typography.Text>

              {item.customer_name && item.property?.full_address && (
                <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{item.customer_name}</div>
              )}

              {item.appointment_date && (
                <div style={{ fontSize: 12, color: '#d46b08', marginTop: 2 }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  Appointment: {dayjs(item.appointment_date).format('MM/DD/YYYY')}
                </div>
              )}

              <div style={{ fontSize: 11, color: '#999', margin: '4px 0' }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                Created {item.created_at ? dayjs(item.created_at).format('MMM D, h:mm A') : ''}
                <span style={{ margin: '0 6px' }}>·</span>
                Job #{item.dispatch_id}
              </div>

              <Space size={6} style={{ marginTop: 4 }}>
                {item.property?.id && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<EnvironmentOutlined />}
                    onClick={() => { setOpen(false); setPinItem(item); }}
                  >
                    Pin on Map
                  </Button>
                )}
                {item.customer_id && (
                  <Button size="small" onClick={() => openCustomer(item)}>
                    Open Customer
                  </Button>
                )}
              </Space>
            </List.Item>
          )}
        />
      </Spin>

      <Divider style={{ margin: '8px 0' }} />
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        The geocoder could not verify these addresses, so their map pins are only its nearest guess —
        techs may be routed to the wrong house. They disappear from this list as soon as the location is pinned.
      </Typography.Text>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomRight"
      >
        <span
          className={styles.action}
          style={{
            cursor: 'pointer',
            display: count === 0 ? 'none' : 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px',
            borderRadius: 4,
            background: '#fff7e6',
            border: '1px solid #ffd591',
            color: '#d46b08',
            fontWeight: 500,
            fontSize: 13,
            lineHeight: '20px',
            whiteSpace: 'nowrap',
          }}
        >
          <EnvironmentOutlined />
          {count > 99 ? '99+' : count} to verify
        </span>
      </Popover>

      <PinAddressModal
        open={!!pinItem}
        property={pinItem?.property ?? undefined}
        onCancel={() => setPinItem(null)}
        onSaved={() => {
          setPinItem(null);
          fetchItems();
          // If the dispatch board is open, refresh its detail panel too.
          window.dispatchEvent(new Event('refreshDispatchEDB'));
        }}
      />
    </>
  );
};

export default UnverifiedAddressBadge;
